import React from 'react';
import { View, Image, TouchableOpacity, ActivityIndicator } from 'react-native';
import Toast from 'react-native-root-toast';
import { useMutation } from '@apollo/client';
import { ErrorCallout, Text, Toggle, TypographyParagraph, WarningCallout } from '../../components';

import { useUserContext } from '../../../common/utils/context';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { ProductMeasurementUnit } from '../product';
import { formatParlevelAndQuantity } from '../../../common/utils/utils';
import { PICK_LIST_ITEM_SET_IS_PICKED_BULK } from '../../../common/graphql/pickListItem';
import usePickListItemAction from '../../hooks/usePickListItemAction';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import { getPickListSummaryExpiryWarningTexts } from './pickListSummaryExpiryWarnings';


const PickListSummaryItem = ({
	item: {
		productId,
		productName,
		productImgUrl,
		quantity,
		pickedQuantity,
		pickedExtraQuantity,
		extraQuantity,
	},
	item,
	hideImages,
	serviceRunId,
	showAllSelections,
	onClickQuantity,
	canPick,
	showExpiryWarnings,
	onOpenExpiryWarningsDialog,
}) => {
	const { pendingItemIds, pickError, runPickAction } = usePickListItemAction();
	const isSavingPick = pendingItemIds.length > 0;
	const [setPickListItemIsPickedBulkMutation] = useMutation(PICK_LIST_ITEM_SET_IS_PICKED_BULK);
	const { tenant, tenantSettings } = useUserContext();

	const setPickListItemIsPickedBulk = async () => {
		if (!canPick) {
			return;
		}

		await runPickAction(item.pickListItemIds, () => setPickListItemIsPickedBulkMutation({
			variables: {
				productId,
				pickListItemIds: item.pickListItemIds,
				isPicked: pickedQuantity + pickedExtraQuantity !== quantity + extraQuantity,
				serviceRunId,
			},
		}));
	};

	const styles = useThemedStyles(createStyles);

	const requiredQuantityStyle = !item.ifAnySelectedItemPicked ? styles.required : styles.requiredDisabled;

	const expiryThresholdDays = tenantSettings?.expiryDateThresholds?.[0] || 7;
	const {
		mandatoryWarningText,
		optionalWarningText,
		expiresBeforeNextServiceText,
		expiresBeforeNextServiceAdditionalText,
	} = getPickListSummaryExpiryWarningTexts({
		expiryWarnings: item.expiryWarnings,
		expiryThresholdDays,
		tenantTimezone: tenant?.timezone,
	});

	const shouldShowExpiryWarnings = Boolean(showExpiryWarnings &&
		item.expiryWarnings &&
		item.expiryWarnings.status !== 'none' &&
		(mandatoryWarningText || optionalWarningText || expiresBeforeNextServiceText));

	return (
		<View style={styles.pickItemWrapper}>
			<View style={styles.pickItem}>

				{!hideImages && (
					<TouchableOpacity onPress={() => Toast.show(productName)} style={styles.imageColumn}>
						<Image
							style={styles.productImg}
							source={{ uri: getProductImageUrl({ imageUrl: productImgUrl }) }}
						/>
					</TouchableOpacity>
				)}

				<View style={styles.mainInformation}>
					<Text style={styles.productName}>
						{productName}
					</Text>
				</View>
				<View style={styles.requiredColumn}>
					<TouchableOpacity
						disabled={!onClickQuantity || isSavingPick}
						onPress={() => (item.ifAnySelectedItemPicked
							? Toast.show('This item is picked. Untick it before changing its quantity.')
							: onClickQuantity(item.pickListItemIds))}
						style={{ flexDirection: 'row' }}
					>
						<TypographyParagraph
							href={!isSavingPick && !item.ifAnySelectedItemPicked && !!onClickQuantity}
							style={[requiredQuantityStyle, item.extraQuantity && styles.extraQuantity]}
							text={formatParlevelAndQuantity(quantity + extraQuantity, item)}
						/>
						{/* {
							Boolean(extraQuantity) && (
								<Text style={styles.extraQuantity}>
									{ ` (+${ formatParlevelAndQuantity(extraQuantity, item) })` }
								</Text>
							)
						} */}
						<ProductMeasurementUnit product={item} />
					</TouchableOpacity>
				</View>
				{
					Boolean(quantity + extraQuantity || showAllSelections) &&  (
						<View style={styles.checkboxColumn}>
							{isSavingPick ? (
								<ActivityIndicator style={{ width: 48, height: 48 }} accessibilityLabel="Saving picked status" />
							) : (
								<Toggle
									onChange={setPickListItemIsPickedBulk}
									partiallyEnabled={pickedQuantity + pickedExtraQuantity > 0 && pickedQuantity + pickedExtraQuantity !== quantity + extraQuantity}
									enabled={pickedQuantity + pickedExtraQuantity === quantity + extraQuantity}
									disabled={!canPick || quantity + extraQuantity === 0}
									key={`${item.id}_${productId}_${quantity}`}
								/>
							)}
						</View>
					)
				}
			</View>
			{isSavingPick && <Text>Saving picked status…</Text>}
			{pickError && <ErrorCallout text={pickError} />}
			{
				pickedQuantity + pickedExtraQuantity > 0 && pickedQuantity + pickedExtraQuantity < quantity + extraQuantity && (
					<View style={styles.pickedQuantityContainer}>
						<Text style={styles.pickedQuantity}>{ `${pickedQuantity + pickedExtraQuantity} picked. ${extraQuantity + quantity - pickedQuantity - pickedExtraQuantity} left.` }</Text>
					</View>
				)
			}
			{
				shouldShowExpiryWarnings && (
					<TouchableOpacity
						onPress={() => onOpenExpiryWarningsDialog && onOpenExpiryWarningsDialog(item)}
						activeOpacity={0.7}
					>
						<View style={styles.expiryWarningsContainer}>
							{mandatoryWarningText && (
								<ErrorCallout
									text={mandatoryWarningText}
								/>
							)}
							{expiresBeforeNextServiceText && (
								<ErrorCallout
									text={expiresBeforeNextServiceText}
									additionalText={expiresBeforeNextServiceAdditionalText}
									rightTextIcon="arrowRight"
								/>
							)}
							{optionalWarningText && (
								<WarningCallout
									text={optionalWarningText}
								/>
							)}
						</View>
					</TouchableOpacity>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, touch } }) => ({
	pickItem: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: touch.min,
	},
	pickItemWrapper: {
		gap: space.xs,
		paddingHorizontal: space.gutter,
		paddingVertical: space.md,
		borderBottomColor: colors.border,
		borderBottomWidth: 1,
	},
	imageColumn: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	productImg: {
		height: 50,
		width: 50,
		resizeMode: 'contain', // Fits image into container with correct aspect ratio
	},
	mainInformation: {
		justifyContent: 'center',
		flex: 1,
	},
	productName: {
		...type.bodyStrong,
		color: colors.text,
	},
	requiredColumn: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	required: {
		...type.cardTitle,
	},
	extraQuantity: {
		color: colors.success,
	},
	requiredDisabled: {
		...type.cardTitle,
		color: colors.text,
	},
	checkboxColumn: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	pickedQuantityContainer: {
		flexDirection: 'row-reverse',
	},
	pickedQuantity: {
		...type.caption,
		color: colors.textSecondary,
	},
	expiryWarningsContainer: {
		marginTop: space.xs,
	},
});

export default PickListSummaryItem;
